import type { IdentitySchema } from '$api-clients/generated';
import type { IdentityRequest } from '$api-clients/hooks';

/**
 * Address as returned by identity REST endpoints (`/teams/locations`,
 * `/teams/{teamId}`), plus a derived single-line `formattedAddress`.
 */
export type PartnerMailingAddress =
  IdentitySchema.components['schemas']['Address'] & {
    formattedAddress: string | null;
  };

type TeamsLocationsItem =
  IdentityRequest<'/teams/locations'>['Data']['data'][number];

/**
 * The raw team fields the partner map needs, projected from the
 * `GET /teams/locations` list item. The `GET /teams` item is a superset, so
 * both endpoints feed `toTeamSearchFields`. Deriving (rather than hand-writing)
 * this means a `/teams/locations` schema change breaks the `Pick` here, and a
 * `/teams` change breaks the `toTeamSearchFields` call site — neither can drift
 * silently.
 */
export type RawTeamFields = Pick<
  TeamsLocationsItem,
  | 'id'
  | 'legalName'
  | 'alias'
  | 'logoUri'
  | 'logoHorizontalUri'
  | 'status'
  | 'mailingAddress'
>;

/**
 * Normalized team shape the partner map renders, sourced from both
 * `GET /teams/locations` and `GET /teams`. `status` (returned by both) drives
 * the org walk's active-team count and root detection; `mailingAddress` carries
 * the derived `formattedAddress`.
 */
export type TeamSearchFields = Omit<
  RawTeamFields,
  'mailingAddress' | 'alias'
> & {
  /** Null when the alias duplicates the legal name (collapsed for display). */
  alias: string | null;
  mailingAddress: PartnerMailingAddress | null;
};

export enum LocationType {
  CURRENT_LOCATION = 'current_location',
  PRIMARY_OFFICE = 'primary_office',
  DEFAULT = 'default',
}

export type InitialLocation = {
  lat: number;
  lng: number;
  type: LocationType;
};

/**
 * Mailing address with the geolocation coerced to numbers for map usage.
 */
export type NumericMailingAddress = Omit<
  PartnerMailingAddress,
  'geolocationLatitude' | 'geolocationLongitude'
> & {
  geolocationLatitude: number;
  geolocationLongitude: number;
};

export type TeamSearchResult = Omit<TeamSearchFields, 'mailingAddress'> & {
  mailingAddress: NumericMailingAddress;
};

/**
 * Subset of address fields used for location matching and pin classification.
 * The string fields are nullable because the community-listings map reuses
 * these helpers (`matches`, `usePinClass`) with looser address data, while the
 * partner map passes the stricter `NumericMailingAddress` (assignable here).
 */
export type MatchableAddress = {
  city: string | null;
  state: string | null;
  county: string | null;
  postalCode: string | null;
  geolocationLatitude: number;
  geolocationLongitude: number;
};
